import React, { useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";

/**
 * PARABOLUM — cinematic studio identity reveal.
 *
 * Premium, physically-inspired intro (Apple keynote / Interstellar energy).
 * No canvas, no shaders, no three.js, no heavy particles — pure DOM + GSAP.
 *
 * Phase 1 (0.0–0.8s) : pure black, a few static stars, the black hole only,
 *                       rotating almost imperceptibly.
 * Phase 2 (0.8–1.8s) : the letters of PARABOLUM travel in from different
 *                       directions and assemble, centered beneath the hole.
 * Phase 3 (1.8–3.2s) : brief hold, then a gentle gravitational pull — the word
 *                       tightens, drifts up and is absorbed into the hole.
 * Phase 4 (3.2–3.9s) : the loader fades and the Hero reveals underneath.
 */

const WORD = "PARABOLUM";

// Each letter drifts in from a slightly different direction (px offsets).
const OFFSETS = [
  { x: -140, y: 110 },  // P — lower-left
  { x: 12, y: -150 },   // A — top
  { x: 160, y: -12 },   // R — right
  { x: -120, y: -92 },  // A — upper-left
  { x: 120, y: 132 },   // B — lower-right
  { x: -18, y: 162 },   // O — bottom
  { x: -162, y: 24 },   // L — left
  { x: 128, y: -112 },  // U — upper-right
  { x: 26, y: -150 },   // M — top
];

function centerOf(el) {
  const r = el.getBoundingClientRect();
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

export default function StudioLoader({ onComplete }) {
  const [show, setShow] = useState(true);
  // Resolve reduced-motion ONCE, synchronously — avoids an async flip that
  // would re-run the effect and interrupt the timeline mid-reveal.
  const reduced = useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );

  const rootRef = useRef(null);
  const starsRef = useRef(null);
  const holeRef = useRef(null);
  const wordRef = useRef(null);
  const lettersRef = useRef([]);
  const doneRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // A small, very subtle field of static stars (DOM, no canvas, no twinkle).
  const stars = useMemo(() => {
    const n = typeof window !== "undefined" && window.innerWidth < 768 ? 26 : 44;
    return Array.from({ length: n }, () => ({
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() < 0.85 ? 1 : 2,
      opacity: 0.08 + Math.random() * 0.28,
    }));
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    const hole = holeRef.current;
    const word = wordRef.current;
    const letters = lettersRef.current.filter(Boolean);
    if (!root || !hole || !word || letters.length === 0) return undefined;

    const finishReady = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      onCompleteRef.current?.();
    };

    const ctx = gsap.context(() => {
      // ----- Reduced motion: quiet, quick reveal -----
      if (reduced) {
        gsap.set(letters, { x: 0, y: 0, opacity: 1, filter: "none" });
        gsap.set(hole, { opacity: 1, scale: 1 });
        gsap.set(starsRef.current, { opacity: 1 });
        const tl = gsap.timeline({ onComplete: () => setShow(false) });
        tl.to(root, {
          opacity: 0,
          duration: 0.5,
          ease: "power2.out",
          delay: 0.8,
          onStart: finishReady,
        });
        return;
      }

      // ----- Initial state -----
      gsap.set(starsRef.current, { opacity: 0 });
      gsap.set(hole, { opacity: 0, scale: 0.9, rotation: 0 });
      letters.forEach((el, i) => {
        gsap.set(el, {
          x: OFFSETS[i % OFFSETS.length].x,
          y: OFFSETS[i % OFFSETS.length].y,
          opacity: 0,
          filter: "blur(9px)",
          scale: 1,
        });
      });

      // Black hole: fade in + an almost imperceptible continuous rotation.
      gsap.to(starsRef.current, { opacity: 1, duration: 1.1, ease: "power1.out" });
      gsap.to(hole, { opacity: 1, scale: 1, duration: 0.9, ease: "power2.out" });
      gsap.to(hole, { rotation: 9, duration: 4.2, ease: "none" });

      const tl = gsap.timeline({ onComplete: () => setShow(false) });

      // PHASE 2 — letters travel in from different directions and assemble.
      tl.to(
        letters,
        {
          x: 0,
          y: 0,
          opacity: 1,
          filter: "blur(0px)",
          duration: 0.82,
          ease: "power3.out",
          stagger: 0.05,
        },
        0.8,
      );

      // PHASE 3a — brief hold, then the gravitational pull begins:
      // the word tightens and drifts gently upward toward the hole.
      tl.to(
        word,
        { y: -18, letterSpacing: "-0.06em", duration: 0.55, ease: "power1.in" },
        2.15,
      );

      // PHASE 3b — absorbed into the hole: each letter converges to the hole
      // centre, shrinking and softening as it's consumed (no flash, no glitch).
      tl.to(
        letters,
        {
          x: (i, el) => centerOf(hole).x - centerOf(el).x,
          y: (i, el) => centerOf(hole).y - centerOf(el).y,
          scale: 0.12,
          opacity: 0,
          filter: "blur(3px)",
          duration: 0.95,
          ease: "power2.in",
          stagger: { each: 0.03, from: "start" },
        },
        2.7,
      );
      // the hole gently swells as it draws the word in.
      tl.to(hole, { scale: 1.06, duration: 0.9, ease: "power2.in" }, 2.85);

      // PHASE 4 — reveal Hero underneath with a soft crossfade (no hard cut).
      tl.add(finishReady, 3.45);
      tl.to(root, { opacity: 0, duration: 0.55, ease: "power2.out" }, 3.45);
    }, rootRef);

    return () => ctx.revert();
  }, [reduced]);

  if (!show) return null;

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[300] flex flex-col items-center justify-center overflow-hidden"
      style={{ background: "#030303" }}
      aria-label="Parabolum"
      role="img"
    >
      {/* subtle static starfield (DOM) */}
      <div ref={starsRef} className="pointer-events-none absolute inset-0" aria-hidden="true">
        {stars.map((s, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              opacity: s.opacity,
            }}
          />
        ))}
      </div>

      {/* the black hole */}
      <img
        ref={holeRef}
        src="/blackhole.webp"
        alt=""
        aria-hidden="true"
        draggable="false"
        className="relative h-[220px] w-[220px] select-none object-contain will-change-transform md:h-[380px] md:w-[380px]"
      />

      {/* wordmark, centered beneath the hole */}
      <div
        ref={wordRef}
        aria-hidden="true"
        className="ff-display relative mt-3 select-none whitespace-nowrap text-center text-4xl font-extrabold tracking-tight text-white md:mt-5 md:text-7xl"
      >
        {WORD.split("").map((ch, i) => (
          <span
            key={i}
            ref={(el) => {
              lettersRef.current[i] = el;
            }}
            className="inline-block will-change-transform"
          >
            {ch}
          </span>
        ))}
      </div>
    </div>
  );
}
